import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { CBadge, CButton, CCol, CFormInput, CRow } from "@coreui/react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEye, faPenToSquare } from "@fortawesome/free-solid-svg-icons";

import TNTable from "@/components/TNTable";
import { LastMessagePreview } from "@/components/weAllTeam/WeAllTeamMessageBody";
import WeAllTeamTabs from "@/components/weAllTeam/WeAllTeamTabs";
import WeAllTeamAvatar from "@/components/weAllTeam/WeAllTeamAvatar";
import {
  formatRelativeTime,
  toBool,
  toNumber,
} from "@/components/weAllTeam/weAllTeamUtils";
import { useTranslationContext } from "@/context/TranslationContext";
import { weAllTeam as tr } from "@/constants/translation/weAllTeam.translation";
import { weAllTeam as routes } from "@/libs/routes";
import constants from "@/libs/constant";
import { useWeAllTeamInbox } from "@/hooks/useWeAllTeam";

/**
 * WeAll Team inbox: every conversation, newest first, with the unread count of each. Kept live by the socket
 * (see WeAllTeamRealtime) - a reply from a user moves its row to the top and raises its unread badge.
 */
const WeAllTeamInbox = () => {
  const t = useTranslationContext();
  const navigate = useNavigate();

  const [currentPage, setCurrentPage] = useState(
    constants.pagination.defaultPage,
  );
  const [searchText, setSearchText] = useState("");
  const [tempSearchText, setTempSearchText] = useState("");
  const searchTimeout = useRef(null);

  useEffect(
    () => () => {
      if (searchTimeout.current) clearTimeout(searchTimeout.current);
    },
    [],
  );

  const handleSearch = (e) => {
    const value = e.target.value;
    setTempSearchText(value);
    if (searchTimeout.current) clearTimeout(searchTimeout.current);
    searchTimeout.current = setTimeout(() => {
      setSearchText(value);
      setCurrentPage(1);
    }, 400);
  };

  const { data, isLoading } = useWeAllTeamInbox({
    page: currentPage,
    limit: constants.pagination.defaultPageSize,
    ...(searchText.trim() ? { search: searchText.trim() } : {}),
  });

  const rows = data?.data?.conversations || [];
  const paginationData = data?.data?.pagination;

  const openConversation = (row) =>
    navigate(routes.conversation.replace(":id", row.conversation_id));

  const columns = useMemo(
    () => [
      {
        id: "user",
        header: t(tr.col_user),
        cell: ({ row }) => {
          const unread = toNumber(row.original.unread_count) > 0;
          return (
            <div
              className="d-flex align-items-center gap-2 weall-team-inbox-user"
              role="button"
              onClick={() => openConversation(row.original)}
            >
              <WeAllTeamAvatar
                src={row.original.user?.profile_photo_url}
                size={36}
              />
              <div>
                <div className={unread ? "fw-bold" : ""}>
                  {row.original.user?.full_name}
                </div>
                {row.original.user?.user_name && (
                  <small className="text-muted">
                    @{row.original.user.user_name}
                  </small>
                )}
              </div>
            </div>
          );
        },
      },
      {
        id: "last_message",
        header: t(tr.col_last_message),
        cell: ({ row }) => {
          const unread = toNumber(row.original.unread_count) > 0;
          return (
            <div
              className={`weall-team-inbox-last ${unread ? "fw-bold" : ""}`}
              title={row.original.last_message}
            >
              {toBool(row.original.last_message_is_from_admin)
                ? t(tr.you_prefix)
                : ""}
              <LastMessagePreview
                row={row.original}
                labels={{
                  photo: t(tr.media_photo),
                  video: t(tr.media_video),
                  pdf: t(tr.media_pdf),
                  document: t(tr.media_document),
                }}
              />
            </div>
          );
        },
      },
      {
        id: "last_message_at",
        header: t(tr.col_last_activity),
        cell: ({ row }) => formatRelativeTime(row.original.last_message_at),
      },
      {
        id: "unread_count",
        header: t(tr.col_unread),
        cell: ({ row }) => {
          const unread = toNumber(row.original.unread_count);
          return unread > 0 ? (
            <CBadge color="danger" shape="rounded-pill">
              {unread}
            </CBadge>
          ) : (
            <span className="text-muted">0</span>
          );
        },
      },
      {
        id: "actions",
        header: t(tr.col_actions),
        cell: ({ row }) => (
          <div className="d-flex gap-3 justify-content-center">
            <div
              className="table-action-text"
              title={t(tr.action_open)}
              onClick={() => openConversation(row.original)}
            >
              <FontAwesomeIcon icon={faEye} className="list-icon" />
            </div>
          </div>
        ),
      },
    ],
    // openConversation only closes over the stable navigate function
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [t],
  );

  return (
    <div className="weall-team-inbox content-moderation-list">
      <WeAllTeamTabs />
      <CRow className="list-align">
        <CCol className="d-flex gap-2 justify-content-start justify-content-md-end flex-wrap align-items-center">
          <CFormInput
            placeholder={t(tr.inbox_search)}
            type="text"
            value={tempSearchText}
            onChange={handleSearch}
            className="filter-field-space form-control-sm"
            style={{ maxWidth: "300px", height: "38px" }}
          />
          <CButton
            color="primary"
            style={{ height: "38px" }}
            onClick={() => navigate(routes.newMessage)}
          >
            <FontAwesomeIcon icon={faPenToSquare} className="me-2" />
            {t(tr.new_message_button)}
          </CButton>
        </CCol>
      </CRow>

      <TNTable
        columns={columns}
        data={rows}
        paginationData={paginationData}
        onSelectPage={(pageIndex) => {
          setCurrentPage(pageIndex + 1);
          window.scrollTo({ top: 0, behavior: "smooth" });
        }}
        pageIndexGet={currentPage - 1}
        isLoading={isLoading}
        t={t}
        responsive={false}
        className="content-moderation-table"
      />
    </div>
  );
};

export default WeAllTeamInbox;
